/**
 * `ThreadSidebar` — left rail listing the user's saved chats grouped
 * by date bucket. Mirrors Taqat's `chat-history-list.tsx`:
 *
 *   ┌──────────────────────────┐
 *   │  + New chat              │  ← shrink-0 header (sticky)
 *   │  🔍 Search chats         │
 *   ├──────────────────────────┤
 *   │  Today                   │  ← group header
 *   │   💬 Receivables snap… 🗑 │  ← single-line truncated title +
 *   │   💬 New invoice flow… 🗑 │    hover-shown trash button
 *   │  Yesterday               │
 *   │   💬 GST filing prep   🗑 │
 *   │  Last 7 days             │
 *   │   …                      │
 *   └──────────────────────────┘
 *
 * Delete goes through a confirm dialog (AlertDialog) so a stray
 * mis-click can't blow away a conversation. On confirm we call
 * `DELETE /api/ai/threads/:id`, optimistically remove the row from
 * the local list, and (if the user was on the deleted thread) start
 * a fresh chat at `/ask/$newId`.
 */
import { useMemo, useState } from "react";
import { MessageSquareIcon, PlusIcon, SearchIcon, Trash2Icon } from "lucide-react";
import {
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogRoot,
  AlertDialogTitle,
  Button,
  cn,
} from "@flint/ui";
import { WEB_ENV } from "../../lib/env.ts";
import { groupThreadsByDay } from "./group-threads.ts";
import type { ThreadSummary } from "./types.ts";

interface ThreadSidebarProps {
  activeId: string;
  threads: ThreadSummary[];
  onSelect: (id: string) => void;
  onNewChat: () => void;
  /** Remove the row from the sidebar list (post-delete). */
  onThreadDeleted: (id: string) => void;
}

export function ThreadSidebar({
  activeId,
  threads,
  onSelect,
  onNewChat,
  onThreadDeleted,
}: ThreadSidebarProps) {
  const [search, setSearch] = useState("");
  const [pendingDelete, setPendingDelete] = useState<ThreadSummary | null>(null);
  const [isDeleting, setIsDeleting] = useState(false);

  const filtered = useMemo(() => {
    const query = search.trim().toLowerCase();
    if (!query) return threads;
    return threads.filter((thread) => thread.title.toLowerCase().includes(query));
  }, [search, threads]);

  const groups = useMemo(() => groupThreadsByDay(filtered), [filtered]);

  async function confirmDelete() {
    if (!pendingDelete) return;
    setIsDeleting(true);
    try {
      const response = await fetch(
        `${WEB_ENV.VITE_FLINT_API_URL}/api/ai/threads/${pendingDelete.id}`,
        {
          method: "DELETE",
          credentials: "include",
        },
      );
      if (response.ok || response.status === 204 || response.status === 404) {
        onThreadDeleted(pendingDelete.id);
      }
    } finally {
      setIsDeleting(false);
      setPendingDelete(null);
    }
  }

  return (
    <>
      <aside className="hidden min-h-0 w-[260px] shrink-0 flex-col bg-bg lg:flex">
        {/* Header: new-chat + search */}
        <div className="shrink-0 space-y-2 px-3 pt-3 pb-2">
          <Button className="w-full gap-2" size="sm" onClick={onNewChat}>
            <PlusIcon className="h-4 w-4" aria-hidden />
            New chat
          </Button>
          <div className="relative">
            <SearchIcon
              className="pointer-events-none absolute left-2.5 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-muted"
              aria-hidden
            />
            <input
              value={search}
              onChange={(event) => setSearch(event.currentTarget.value)}
              className="h-8 w-full rounded-md border border-line bg-paper pl-8 pr-3 text-xs text-ink outline-none placeholder:text-muted focus:border-sky-300 focus:ring-2 focus:ring-sky-100"
              placeholder="Search chats"
              type="search"
              aria-label="Search chats"
            />
          </div>
        </div>

        {/* Thread list */}
        <div className="min-h-0 flex-1 overflow-y-auto px-2 pb-2">
          {threads.length === 0 ? (
            <EmptyHint>Your saved chats will appear here after the first response.</EmptyHint>
          ) : filtered.length === 0 ? (
            <EmptyHint>No chats match that search.</EmptyHint>
          ) : (
            groups.map((group) => (
              <div key={group.key} className="mt-3 first:mt-0">
                <div className="px-2 py-1 text-xs font-medium uppercase tracking-wide text-muted">
                  {search.trim() ? "Search results" : group.label}
                </div>
                <ul className="space-y-px">
                  {group.threads.map((thread) => (
                    <ThreadRow
                      key={thread.id}
                      thread={thread}
                      isActive={thread.id === activeId}
                      onSelect={onSelect}
                      onRequestDelete={setPendingDelete}
                    />
                  ))}
                </ul>
              </div>
            ))
          )}
        </div>
      </aside>

      <AlertDialogRoot
        open={pendingDelete !== null}
        onOpenChange={(open) => {
          if (!open) setPendingDelete(null);
        }}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete chat</AlertDialogTitle>
            <AlertDialogDescription>
              {pendingDelete
                ? `"${pendingDelete.title}" and all its messages will be permanently removed. This can't be undone.`
                : ""}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel asChild>
              <Button intent="ghost" disabled={isDeleting}>
                Cancel
              </Button>
            </AlertDialogCancel>
            <AlertDialogAction asChild>
              <Button
                intent="danger"
                disabled={isDeleting}
                onClick={(event) => {
                  event.preventDefault();
                  void confirmDelete();
                }}
              >
                {isDeleting ? "Deleting…" : "Delete"}
              </Button>
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialogRoot>
    </>
  );
}

interface ThreadRowProps {
  thread: ThreadSummary;
  isActive: boolean;
  onSelect: (id: string) => void;
  onRequestDelete: (thread: ThreadSummary) => void;
}

/**
 * One row in the thread list. The whole row is the navigate target;
 * the trash button at the right is a sibling button so its click
 * doesn't bubble into the row's `onClick`. The trash fades in only on
 * `group-hover`, matching Taqat's `showOnHover` Sidebar action.
 */
function ThreadRow({ thread, isActive, onSelect, onRequestDelete }: ThreadRowProps) {
  return (
    <li
      className={cn(
        "group relative rounded-md text-xs leading-snug transition-colors hover:bg-paper",
        isActive && "bg-paper text-ink shadow-sm",
      )}
    >
      <button
        type="button"
        onClick={() => onSelect(thread.id)}
        className="flex w-full min-w-0 items-center gap-1.5 pl-2 pr-7 py-1.5 text-left text-ink cursor-pointer"
        title={thread.title}
      >
        {thread.icon ? (
          <span className="shrink-0 text-xs leading-none" aria-hidden>{thread.icon}</span>
        ) : (
          <MessageSquareIcon className="h-3.5 w-3.5 shrink-0 text-muted" aria-hidden />
        )}
        <span className="min-w-0 flex-1 truncate">{thread.title}</span>
      </button>

      <button
        type="button"
        onClick={(event) => {
          event.stopPropagation();
          onRequestDelete(thread);
        }}
        aria-label={`Delete chat: ${thread.title}`}
        className={cn(
          "absolute right-1 top-1/2 -translate-y-1/2 grid h-6 w-6 place-items-center rounded text-muted opacity-0 transition-opacity hover:bg-line hover:text-bad",
          "group-hover:opacity-100 focus-visible:opacity-100",
        )}
      >
        <Trash2Icon className="h-3 w-3" aria-hidden />
      </button>
    </li>
  );
}

function EmptyHint({ children }: { children: React.ReactNode }) {
  return (
    <div className="mt-2 rounded-md border border-dashed border-line px-3 py-4 text-xs leading-5 text-muted">
      {children}
    </div>
  );
}
